/** «Лиды Авито» / «Лиды ВК» — ровно то, что отдаёт `GET /api/leads/avito?source=`. */
import type { Comment } from "@/components/Comments";
import { apiFetch, query } from "@/lib/api";

export type Topic = "dress" | "sell" | "other";
export type LeadSource = "avito" | "vkgroup";

export type AvitoDay = {
  /** День или первый день недели (понедельник, обрезан началом периода). */
  day: string | null;
  /** Только у недели: последний её день в периоде. */
  to?: string;
  total: number;
  topics: Record<Topic, number>;
  by_model: number;
  /** Обращений, чей чат или лид получил автодожим (видео + текст). */
  followup: number;
  /** `no_lead` — хотят платье, а ни лида, ни новой сделки нет; `bron` — сделка
   *  побывала в «Забронировано», `contract` — договор перешёл в «Бронь»/«Подписан». */
  dress: {
    total: number;
    phone: number;
    deal: number;
    booked: number;
    bron: number;
    contract: number;
    no_lead: number;
  };
};

export type AvitoRow = {
  session_id: number;
  /** Все сессии обращения (чат за день) — для переписки; у обращения из «Чатов» пусто. */
  session_ids: number[];
  /** Диалог «Чатов» (`/chats/<id>`), если обращение из своей ленты (с 02.10.26); иначе null. */
  own_chat: number | null;
  /** До какого момента переписка этого обращения (начало следующего); null — до конца. */
  own_to: string | null;
  /** Ключ комментариев: чат открытой линии Bitrix, а без него — диалог «Чатов» со сдвигом. */
  chat_id: number;
  /** Комментарии сотрудников к чату — общие для всех его обращений. */
  comments: Comment[];
  /** Каналы чата: `channel` Авито / `peer` ВК — ключ решений дожима. */
  chats: string[];
  day: string;
  at: string;
  name: string;
  topic: Topic;
  topic_source: "claude" | "rules";
  quote: string;
  fetched: boolean;
  client_messages: number;
  /** Последняя реплика клиента или оператора (системные не в счёт). */
  last: {
    author: "client" | "operator";
    text: string;
    at: string | null;
  } | null;
  item: string;
  lead_id: number | null;
  /** Статус лида CRM (`b24lead_status.leadStatus`). */
  lead_status: string | null;
  phone: boolean;
  /** Сделка обращения: по цепочке чат → лид → сделка или по номеру, создана не раньше дня обращения. */
  deal: { id: number; stage: string; stage_name: string | null } | null;
  /** Старая сделка, к которой Bitrix сам привязал чат (незакрытая сделка контакта) — не в счёт. */
  old_deal: {
    id: number;
    stage: string;
    stage_name: string | null;
    created: string;
  } | null;
  /** Хочет платье, а ни лида, ни новой сделки нет — обращение мимо воронки. */
  no_lead: boolean;
  booked_at: string | null;
  /** Первый после начала диалога переход сделки в «Забронировано». */
  bron_at: string | null;
  /** Первое заключение договора сделки («Бронь»/«Подписан»). */
  contract_at: string | null;
  /** Последний автодожим по чату или лиду (журналы avito/vk-followup, chat-send). */
  followup_at: string | null;
  /** Последнее видео салона (шаг «видео» или видео + текст до 30.09). */
  followup_video_at: string | null;
};

export type AvitoLeads = {
  source: LeadSource;
  channel: string;
  from: string;
  to: string;
  topics: Record<Topic, string>;
  wait_stage: string;
  total: AvitoDay;
  days: AvitoDay[];
  weeks: AvitoDay[];
  /** Месяц с 1-го, крайние обрезаны периодом (`day`..`to`). */
  months: AvitoDay[];
  rows: AvitoRow[];
};

export function loadAvitoLeads(
  from: string,
  to: string,
  source: LeadSource = "avito",
): Promise<AvitoLeads> {
  return apiFetch<AvitoLeads>(`/api/leads/avito${query({ from, to, source })}`);
}

export type OwnMessage = {
  id: number;
  date: string;
  author: "client" | "operator";
  name: string;
  text: string;
};

/** Переписка обращения из своей ленты «Чатов». */
export function loadOwnDialog(
  chat: number,
  from: string,
  to: string | null,
): Promise<{ messages: OwnMessage[] }> {
  return apiFetch<{ messages: OwnMessage[] }>(
    `/api/leads/avito/dialog${query({ chat, from, to })}`,
  );
}

export function addChatComment(
  chatId: number,
  text: string,
): Promise<Comment[]> {
  return apiFetch<Comment[]>("/api/leads/avito/comment", {
    method: "POST",
    body: JSON.stringify({ chat_id: chatId, text }),
  });
}

/** Решение рассылки по одному чату: `reason` "" — уйдёт, иначе почему нет. */
export type FollowupDecision = {
  chat: string;
  type: "lead" | "deal";
  target_id: number | null;
  status: string | null;
  status_name: string | null;
  topic: Topic | null;
  reason: string;
  /** Старая сделка, которую Bitrix сам привязал к чату: целью не считаем. */
  old_deal?: number | null;
};

/** Шаг дожима: «видео» — хотящим платье за 3 дня сразу; «текст» — вопрос о записи молчащим. */
export type FollowupStage = "video" | "text";

/** Задача дожима: одна на все каналы; `output` — хвост вывода раннера, `result` — решения предпросмотра. */
export type FollowupJob = {
  pid: number;
  source: LeadSource;
  stage?: FollowupStage;
  send: boolean;
  who: string;
  started_at: string;
  running: boolean;
  output: string[];
  result: { checked_at: string; decisions: FollowupDecision[] } | null;
};

/** Последняя проверка по `<канал>:<шаг>` — не пропадает после отправки. */
export type FollowupCheck = {
  checked_at: string;
  decisions: FollowupDecision[];
};

export type FollowupState = {
  job: FollowupJob | null;
  checks?: Record<string, FollowupCheck>;
};

export function loadFollowup(): Promise<FollowupState> {
  return apiFetch<FollowupState>("/api/leads/followup");
}

export function startFollowup(
  source: LeadSource,
  send: boolean,
  stage: FollowupStage,
): Promise<FollowupState> {
  return apiFetch<FollowupState>("/api/leads/followup", {
    method: "POST",
    body: JSON.stringify({ source, send, stage }),
  });
}
